import _ from 'lodash'
import qs from 'querystring'
import request from '@/utils/request'
import store from '@/store'
import { getUserInfo } from '@/api/auth'
import { API_PREFIX, SET_USER_INFO } from '@/utils/constants'


/**
 * @api /api/v1.Department/departmentList
 * @method GET
 * @description 获取项目下的所有部门
 *
 */
export async function getDepartmentTree() {
  try {
    const projectName = store.state.userInfo.projectName
    const res = await request.get(`${API_PREFIX}/api/v1.Department/departmentList`)
    return [{
      id: 0,
      name: projectName,
      end: 0,
      staff: 0,
      children: res.data,
    }]
  } catch (e) {
    return []
  }
}

/**
 * @api /api/v1.Department/departmentInfo
 * @method GET
 * @description 获取部门详细信息
 *
 * @param {number} id 部门 ID
 * @param {number} type 类型，1: 组织; 2: 部门
 * @param {string} search 用于用户列表搜索
 *
 */
export async function get(params) {
  try {
    const query = {
      id: params.id || parseInt(store.state.userInfo.belong, 10),
      type: params.id === 0 ? 1 : 2,
      total: 99999, // 不需要分页，故传一个很大的每页记录数
    }
    const qstr = qs.stringify(query)
    const res = await request.get(`${API_PREFIX}/api/v1.Department/departmentInfo?${qstr}`)
    const data = res.data
    const output = {
      id: params.id,
      parent: params.pid || 0,
      name: params.name,
      refId: data.number || '',
      note: data.remark || data.description || '',
      project_unit_name:data.project_unit_name,       //添加project_unit_name单位名称字段
      children: params.children,
    }
    if (data.type === 1) { // 有下级部门
      const departments = data.departmentInfo
      output.staffCount = departments.reduce((sum, current) => sum + current.staff, 0)
      output.departmentCount = departments.length
      output.departments = departments
      output.staffs = []
    } else if (data.type === 2) { // 含有用户
      const staffs = data.staffList.data
      output.staffCount = staffs.length
      output.departmentCount = 0
      output.departments = []
      output.staffs = staffs.map((item, index) => ({
        id: item.id,
        index: index + 1,
        username: item.userName,
        name: item.name,
        mobilePhone: item.mobile,
        roles: item.position,
      }))
    } else { // 既没有下级部门，也不含有用户
      output.staffCount = 0
      output.departmentCount = 0
      output.departments = []
      output.staffs = []
    }
    output.shownStaffs = _.clone(output.staffs)
    return output
  } catch (e) {
    return null
  }
}

/**
 * @api /api/v1.Department/editDepartment
 * @method POST
 * @description 更新部门信息
 *
 * @param {number} id 部门 ID
 * @param {number} pid 上级部门 ID
 * @param {string} name 部门名称
 * @param {string} remark 备注
 *
 */
export async function update(params) {
  try {
    if (params.id === 0) {
      const query = {
        id: parseInt(store.state.userInfo.belong, 10),      //缺少project_unit_name	string	是	单位名称 --参数--导致测试版PC端，用户权限-权限管理-组织机构-选择项目点击编辑报错
        name: params.name,
        description: params.note,
        project_unit_name:params.project_unit_name
      }
      await request.post(`${API_PREFIX}/api/v1.Project/editProject`, query)
      const userInfo = await getUserInfo()
      store.commit(SET_USER_INFO, userInfo)
    } else {
      const query = {
        id: params.id,
        pid: params.parent.id,
        name: params.name,
        remark: params.note,
      }
      await request.post(`${API_PREFIX}/api/v1.Department/editDepartment`, query)
    }
    return true
  } catch {
    return false
  }
}

/**
 * @api /api/v1.Department/addDepartment
 * @method POST
 * @description 添加子部门
 *
 * @param {number} pid 上级部门 ID，顶级传 0
 * @param {string} name 部门名称
 * @param {string} remark 备注
 *
 */
export async function createSubdepartment(params) {
  try {
    const query = {
      pid: params.parent,
      name: params.name,
      remark: params.note,
    }
    await request.post(`${API_PREFIX}/api/v1.Department/addDepartment`, query)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Department/delDepartment
 * @method GET
 * @description 删除部门
 *
 * @param {number} id 部门 ID
 *
 */
export async function remove(params) {
  try {
    const query = {
      id: params.id,
    }
    const qstr = qs.stringify(query)
    await request.get(`${API_PREFIX}/api/v1.Department/delDepartment?${qstr}`)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Department/delDepartmentStaffs
 * @method POST
 * @description 删除部门下的用户
 *
 * @param {number[]} userid 用户 ID 数组
 * @param {number} id 部门 ID
 *
 */
export async function removeStaffs(params) {
  try {
    const query = {
      id: params.departmentId,
      userid: params.userIds,
    }
    await request.post(`${API_PREFIX}/api/v1.Department/delDepartmentStaffs`, query)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Department/staffNotInThisDepartment
 * @method GET
 * @description 获取不在某部门下的用户
 *
 * @param {number} id 部门 ID
 * @param {number} page 页码
 * @param {number} total 每页记录数
 * @param {string} search 职员搜索关键字
 *
 */
export async function findExternalStaffs(params) {
  try {
    const query = {
      id: params.id,
      page: params.page,
      total: params.pageSize,
    }
    if (params.keyword) {
      query.search = params.keyword
    }
    const qstr = qs.stringify(query)
    const res = await request.get(`${API_PREFIX}/api/v1.Department/staffNotInThisDepartment?${qstr}`)
    const paginate = res.data.page
    const list = res.data.data
    return {
      page: paginate.current_page,
      pageSize: paginate.per_page,
      total: paginate.total,
      totalPages: paginate.last_page,
      results: list.map((item, index) => ({
        index: (paginate.current_page - 1) * paginate.per_page + index + 1,
        id: item.id,
        username: item.userName,
        name: item.name,
        mobilePhone: item.mobile,
      })),
    }
  } catch (e) {
    return {
      page: params.page,
      pageSize: params.pageSize,
      total: 0,
      totalPages: 1,
      results: [],
    }
  }
}

/**
 * @api /api/v1.Department/linkDepartmentAndStaff
 * @method POST
 * @description 添加用户到部门
 *
 * @param {number[]} userid 用户 ID 数组
 * @param {number} id 部门 ID
 *
 */
export async function addStaffs(params) {
  try {
    const query = {
      id: params.departmentId,
      userid: params.userIds,
    }
    await request.post(`${API_PREFIX}/api/v1.Department/linkDepartmentAndStaff`, query)
    return true
  } catch (e) {
    return false
  }
}
/**
 * @api /api/v1.Department/sortDepartment
 * @method get
 * @description 部门排序
 *
 * @param  id 被拖动部门id
 * @param  target_id id 	目标位置的部门id
 * @param  move_type  	1目标部门的上面2目标部门的下面
 *
 */
export function sortDepartment(obj){
  return request({
    url: `${API_PREFIX}/api/v1.Department/sortDepartment`,
    method: 'get',
    params: obj,
  })
}